import {useAppOrigin} from '~/app/hooks/use-app-origin'
import axios from 'axios'
import {useQuery, useMutation, useQueryClient} from '@tanstack/react-query'

const annexProxy = (origin, endpoint, options, param) =>
    axios.get(`${origin}/api/annexcloud`, {
        params: {
            endpoint,
            options,
            param
        }
    })

export const useCheckEnrollment = (emailId, siteId, {enabled = true} = {}) => {
    const appOrigin = useAppOrigin()
    const query = useQuery({
        queryKey: ['checkEnrollment', emailId, siteId],
        queryFn: async () => {
            return await annexProxy(appOrigin, `/api/3.0/users/${emailId}`, {}, emailId)
        },
        enabled: !!emailId && !!siteId && enabled
    })

    return query
}

export const useCheckTier = (emailId, siteId, {enabled = true} = {}) => {
    const appOrigin = useAppOrigin()
    const query = useQuery({
        queryKey: ['checkTier', emailId, siteId],
        queryFn: async () => {
            return await annexProxy(appOrigin, `/api/3.0/users/${emailId}/tiers`, {}, emailId)
        },
        enabled: !!emailId && !!siteId && enabled
    })

    return query
}

export const useAwardPoints = () => {
    const queryClient = useQueryClient()
    const appOrigin = useAppOrigin()

    return useMutation(
        ({emailId, groupId, actionId, availablePointsData}) => {
            if (!emailId || !groupId || !actionId || !availablePointsData) {
                console.error('Required data missing for awarding points.')
                return
            }
            const actionGroup = availablePointsData.actionGroupDetail.find(
                (_actionGroup) => _actionGroup.groupId === groupId
            )
            if (!actionGroup) {
                console.error(`No action group found for groupId: ${groupId}`)
                return
            }
            const actionData = actionGroup.groupActionData.find(
                (_actionData) => _actionData.actionId === actionId
            )
            if (!actionData) {
                console.error(`No action data found for actionId: ${actionId}`)
                return
            }
            const requestPayload = {
                id: emailId,
                actionId: actionId,
                credit: actionData.actionPoints,
                activity: 'CREDIT',
                reason: actionData.actionName
            }

            return annexProxy(appOrigin, `/api/3.0/points`, {
                method: 'POST',
                body: requestPayload
            })
        },
        {
            onSuccess: (data, variables) => {
                queryClient.invalidateQueries({
                    queryKey: ['availableToEarnPoints', variables.emailId, variables.siteId],
                    exact: true
                })
                queryClient.invalidateQueries({
                    queryKey: ['availablePoints', variables.emailId, variables.siteId],
                    exact: true
                })
                queryClient.invalidateQueries({
                    queryKey: ['checkTier', variables.emailId, variables.siteId],
                    exact: true
                })
            },
            onError: (error) => {
                console.log('An error occurred while updating loyalty points: ', error)
            }
        }
    )
}

export const useCheckAvailableToEarnPoints = (emailId, siteId, {enabled = true} = {}) => {
    const appOrigin = useAppOrigin()
    const query = useQuery({
        queryKey: ['availableToEarnPoints', emailId, siteId],
        queryFn: async () => {
            return await annexProxy(appOrigin, `/api/3.0/actiongroup/${emailId}`, {}, emailId)
        },
        enabled: !!emailId && !!siteId && enabled
    })

    return query
}

export const useAvailablePoints = (emailId, siteId, {enabled = true} = {}) => {
    const appOrigin = useAppOrigin()
    const query = useQuery({
        queryKey: ['availablePoints', emailId, siteId],
        queryFn: async () => {
            return await annexProxy(appOrigin, `/api/3.0/points/${emailId}`, {}, emailId)
        },
        enabled: !!emailId && !!siteId && enabled
    })

    return query
}
